import React, { useState, PureComponent } from "react";
import "./filters.css";
import { Card, Col, Form, Alert, Spinner, Button, Row } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { IoMdClose } from "react-icons/io";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiAnalyse } from "react-icons/bi";
import { industriesNew } from "../../dropdown-options/industries";
import { countries } from "../../dropdown-options/countries";
import { entityTypes } from "../../dropdown-options/entity-types";
import { reasonsForShutdown } from "../../dropdown-options/reasons-for-shutdown";
import { lifespans } from "../../dropdown-options/lifespans";
import { fundingAmounts } from "../../dropdown-options/funding-amounts";
import { genders } from "../../dropdown-options/genders";
import { noOfFounders } from "../../dropdown-options/no-of-founders";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * CustomizedAxisTick Component
 * Custom chart axis tick label renderer for Recharts bar charts with label rotation
 */
class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, stroke, payload } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" transform="rotate(-20)">
          {payload.value}
        </text>
      </g>
    );
  }
}

/**
 * FundingRounds Filter Component
 * 
 * Analyzes startup funding types and rounds received (Angel, Seed, Series A/B/C, etc).
 * Shows frequency and percentage of startups in each funding round category.
 * Includes filters for entity type, shutdown reasons, lifespan, location, industry, and founder demographics.
 * 
 * @param {Object} props - Component props including Supabase client
 * @param {Object} props.supabase - Supabase client instance
 * @returns {JSX.Element} Funding round distribution chart
 */
const FundingRounds = (props) => {
  // State to control alert visibility
  const [alert, setAlert] = useState(false);

  // State to store alert message content
  const [message, setMessage] = useState("");

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // Get current user and Supabase client from context
  const { currentUser, supabase } = useSupabaseApp();

  // Check if user is authenticated; redirect if not
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // State to manage filter form inputs
  const [filters, setFilters] = useState({
    type: "Funding status",
    entityType: "Entity type",
    reasons: "Reasons for shutdown",
    lifespan: "Lifespan",
    location: "Location",
    industry: "Industry",
    fundingAmount: "Funding amount",
    gender: "Gender",
    numOfFounders: "No. of founders",
  });

  // State to store chart data - startups by funding round type
  const [investments, setInvestments] = useState([
    {
      "Funding Round": "Angel",
      Frequency: 1,
      "%": 0.01,
    },
    {
      "Funding Round": "Convertible Note",
      Frequency: 117,
      "%": 1.33,
    },
    {
      "Funding Round": "Corporate Round",
      Frequency: 8,
      "%": 0.09,
    },
    {
      "Funding Round": "Debt Financing",
      Frequency: 371,
      "%": 4.22,
    },
    {
      "Funding Round": "Equity Crowdfunding",
      Frequency: 225,
      "%": 2.56,
    },
    {
      "Funding Round": "Funding Round",
      Frequency: 84,
      "%": 0.96,
    },
    {
      "Funding Round": "Grant",
      Frequency: 172,
      "%": 1.96,
    },
    {
      "Funding Round": "Initial Coin Offering",
      Frequency: 8,
      "%": 0.09,
    },
    {
      "Funding Round": "Non-Equity Assistance",
      Frequency: 93,
      "%": 1.06,
    },
    {
      "Funding Round": "Post-IPO Debt",
      Frequency: 2,
      "%": 0.02,
    },
    {
      "Funding Round": "Post-IPO Equity",
      Frequency: 18,
      "%": 0.2,
    },
    {
      "Funding Round": "Pre-Seed",
      Frequency: 1,
      "%": 0.01,
    },
    {
      "Funding Round": "Product Crowdfunding",
      Frequency: 17,
      "%": 0.19,
    },
    {
      "Funding Round": "Secondary Market",
      Frequency: 10,
      "%": 0.11,
    },
    {
      "Funding Round": "Seed",
      Frequency: 1,
      "%": 0.01,
    },
    {
      "Funding Round": "Series A",
      Frequency: 894,
      "%": 10.17,
    },
    {
      "Funding Round": "Series B",
      Frequency: 549,
      "%": 6.24,
    },
    {
      "Funding Round": "Series C",
      Frequency: 212,
      "%": 2.41,
    },
    {
      "Funding Round": "Series D",
      Frequency: 89,
      "%": 1.01,
    },
    {
      "Funding Round": "Series E",
      Frequency: 26,
      "%": 0.3,
    },
    {
      "Funding Round": "Series F",
      Frequency: 8,
      "%": 0.09,
    },
    {
      "Funding Round": "Series G",
      Frequency: 2,
      "%": 0.02,
    },
    {
      "Funding Round": "Venture Round",
      Frequency: 42,
      "%": 0.48,
    },
    {
      "Funding Round": "Seed Round",
      Frequency: 3313,
      "%": 37.68,
    },
    {
      "Funding Round": "Private Equity Round",
      Frequency: 1778,
      "%": 20.22,
    },
    {
      "Funding Round": "Angel Round",
      Frequency: 588,
      "%": 6.69,
    },
    {
      "Funding Round": "Funding round",
      Frequency: 106,
      "%": 1.21,
    },
    {
      "Funding Round": "Pre-Seed Round",
      Frequency: 26,
      "%": 0.3,
    },
    {
      "Funding Round": "Undisclosed",
      Frequency: 26,
      "%": 0.3,
    },
    {
      "Funding Round": "Acquisition",
      Frequency: 2,
      "%": 0.02,
    },
    {
      "Funding Round": "Crowd SAFE",
      Frequency: 2,
      "%": 0.02,
    },
    {
      "Funding Round": "Crowdfunding",
      Frequency: 1,
      "%": 0.01,
    },
  ]);

  // Reset all filter form inputs to their default values and close any alert
  function resetFilters(e) {
    e.preventDefault();
    setFilters({
      type: "Funding status",
      entityType: "Entity type",
      reasons: "Reasons for shutdown",
      lifespan: "Lifespan",
      location: "Location",
      industry: "Industry",
      fundingAmount: "Funding amount",
      gender: "Gender",
      numOfFounders: "No. of founders",
    });
    setAlert(false);
  }

  // Update filter state whenever user changes a form input value
  function changeFilters(e) {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    // Create a copy of current filters object
    let filtersCopy = { ...filters };

    // Update the changed filter property
    filtersCopy[name] = value;
    setFilters({ ...filtersCopy });
  }

  // Submit filters to Supabase edge function and fetch funding round distribution data
  function submitFilters(e) {
    // Show loading alert while fetching data
    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Loading
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            Won't be long now. We're always doing our best to reduce waiting
            time.
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            If your waiting time is too long,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>
    );

    supabase.functions
      .invoke("postmortemAnalysis", { body: filters })
      .then((response) => {
        let graphData = response.data;
        setInvestments(graphData);
        closeAlert();
      })
      .catch((error) => {
        setAlert(true);
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              Your filter query returned no results. Please change your filter
              values and try again.
            </p>
          </div>
        );
      });
    e.preventDefault();
  }

  return (
    <div className="accordion-card-body">
      {/* loading alert */}

      <div className="col-12 div-filter">
        {/* form */}
        <Form onSubmit={submitFilters}>
          <Form.Group className="inputgroup">
            <Row className="align-items-center">
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.entityType}
                  name="entityType"
                  onChange={changeFilters}
                >
                  <option>Entity type</option>
                  {entityTypes.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.reasons}
                  name="reason"
                  onChange={changeFilters}
                >
                  <option>Reasons for shutdown</option>
                  {reasonsForShutdown.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.lifespan}
                  name="lifespan"
                  onChange={changeFilters}
                >
                  <option>Lifespan</option>
                  {lifespans.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.location}
                  name="location"
                  onChange={changeFilters}
                >
                  <option>Location</option>
                  {countries.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.industry}
                  name="industry"
                  onChange={changeFilters}
                >
                  <option>Industry</option>
                  {industriesNew.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.fundingAmount}
                  name="fundingAmount"
                  onChange={changeFilters}
                >
                  <option>Funding amount</option>
                  {fundingAmounts.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.gender}
                  name="gender"
                  onChange={changeFilters}
                >
                  <option>Gender</option>
                  {genders.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.numOfFounders}
                  name="numOfFounders"
                  onChange={changeFilters}
                >
                  <option>No. of founders</option>
                  {noOfFounders.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>

              {/* reset button */}
              <Col xs="auto">
                <button
                  onClick={resetFilters}
                  className="reset-button tour-clear-filter"
                >
                  <span>Clear</span>
                </button>
              </Col>
              {/* submit button */}
              <Col xs="auto">
                <button type="submit" className="submit-button">
                  <span>Filter</span>
                </button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </div>

      {/* results */}
      <div className="col-12">
        <Card className="graph-card">
          <div>
            {/* chart component */}
            <ResponsiveContainer id="vc-charts">
              <BarChart
                data={investments}
                margin={{ top: 20, right: 20, left: 20, bottom: 100 }}
                width={1100}
                height={500}
                fontFamily="AileronRegular"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="Funding Round"
                  interval={0}
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  tick={<CustomizedAxisTick />}
                  label={{
                    value: "Funding Rounds",
                    offset: -80,
                    position: "insideBottom",
                  }}
                />
                <YAxis
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  label={{
                    value: "Frequency",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip />
                <Bar dataKey="Frequency" fill="#8884d8" barSize={50}>
                  <LabelList dataKey="%" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* notes */}
      <div className="col-12 extra-notes">
        <span className="info-notes">
          Note:
          <ul>
            <li id="notes-points">
              Based on{" "}
              {props.companiesWithFundingRounds
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies with recorded{" "}
              <span style={{ fontFamily: "AileronItalic" }}>
                funding round data
              </span>
              .
            </li>
            <li id="notes-points">
              In due time, the remaining{" "}
              {props.companiesWithFundingRounds
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies without funding data will be identified and
              recorded by our algorithms.
            </li>
            <li id="notes-points">
              Top 10 funding rounds (
              <span style={{ fontFamily: "AileronItalic" }}>
                in order from highest to lowest no. of last funding raised
              </span>
              ): seed, private equity, early stage, M&A, late stage, equity
              crowdfunding, grant, funding round, convertible note, initial
              public offering.
            </li>
          </ul>
        </span>
      </div>
    </div>
  );
};

export default FundingRounds;
